/**
 * chargesApi — acciones de cargo vía payment (portal de pagos).
 *
 * `retryCharge` reintenta un cargo llamando a payment `POST /api/charge-retry`
 * (que acepta SERVICE_SECRET o Clerk-staff). Obtiene el token de Clerk de forma
 * imperativa (window.Clerk) para no tener que pasar `getToken` a los componentes
 * anidados de la lista de cargos. `chargeId` = subscription_charges.id (interno).
 * La URL de yoolpay se deriva del hostname en runtime (ver baseUrl.js).
 *
 * `listCharges` y `getCharge` son la LECTURA, y también salen por payment en vez de leer
 * `company_subscription_charges` desde Supabase. POR QUÉ: de las cinco tablas que arma esa
 * pantalla, `subscription_charges` es la única sin policy de lectura general —solo tiene
 * las `*_company`, que filtran por `empresa_usuarios`, del portal de clientes empresa— así
 * que el staff veía cero filas. Y no se arregla con una policy que reconozca al staff: el
 * token por defecto de Clerk solo trae `sub`, sin email ni rol, así que el RLS no tiene con
 * qué distinguirlo (mismo motivo por el que payment resuelve el email contra la API de
 * Clerk). Ver payment/src/workers/charges.worker.ts.
 */

import { yoolpayBaseUrl } from './baseUrl.js';

const YOOLPAY_BASE_URL = yoolpayBaseUrl();

async function clerkToken() {
  try {
    return (await window?.Clerk?.session?.getToken?.()) || null;
  } catch {
    return null;
  }
}

/** GET a payment con el JWT de staff; devuelve el body ya parseado o lanza Error. */
async function get(path) {
  const token = await clerkToken();
  const res = await fetch(`${YOOLPAY_BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.error || `payment respondió ${res.status}`);
  }
  return body;
}

/**
 * Todos los cargos, del más reciente al más antiguo. La pantalla filtra y pagina en el
 * cliente, así que se trae el histórico completo (payment lo topa en 5.000 filas).
 */
export async function listCharges() {
  const body = await get('/api/charges');
  return Array.isArray(body?.charges) ? body.charges : [];
}

/**
 * Un cargo puntual. `identificador` puede ser el `id` interno (uuid) o el
 * `external_charge_id` del PSP — payment prueba ambos. Devuelve `null` si no existe.
 */
export async function getCharge(identificador) {
  try {
    const body = await get(`/api/charges?id=${encodeURIComponent(identificador)}`);
    return body?.charge ?? null;
  } catch (error) {
    // "no encontrado" no es una falla de la pantalla: es una fila que no está.
    if (/404|no encontrado/i.test(error?.message || '')) return null;
    throw error;
  }
}

/** Reintenta un cargo puntual vía payment. `chargeId` = subscription_charges.id. */
export async function retryCharge(chargeId) {
  const token = await clerkToken();
  const res = await fetch(`${YOOLPAY_BASE_URL}/api/charge-retry`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ chargeId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.error || `payment respondió ${res.status}`);
  }
  return body;
}
